import { Directive, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuillEditorComponent } from 'ngx-quill';
import Quill from 'quill';

/**
 * Directive that enables pasting raw HTML source code into a Quill editor.
 *
 * When users paste text that looks like HTML (contains tags), this directive
 * intercepts the paste event and renders the HTML instead of escaping it.
 *
 * Usage:
 * ```html
 * <quill-editor libQuillHtmlPaste ...></quill-editor>
 * ```
 */
@Directive({
  selector: 'quill-editor[libQuillHtmlPaste]',
  standalone: true,
})
export class QuillHtmlPasteDirective {
  private readonly quillEditor = inject(QuillEditorComponent);

  constructor() {
    this.quillEditor.onEditorCreated
      .pipe(takeUntilDestroyed())
      .subscribe((quill: Quill) => {
        this.setupHtmlPasteHandler(quill);
      });
  }

  private setupHtmlPasteHandler(quill: Quill): void {
    quill.root.addEventListener(
      'paste',
      (e: ClipboardEvent) => {
        const clipboardData = e.clipboardData;
        if (!clipboardData) return;

        const pastedText = clipboardData.getData('text/plain').trim();
        // Check if the pasted text looks like HTML (contains tags)
        if (/<[a-z][\s\S]*>/i.test(pastedText)) {
          e.preventDefault();
          e.stopImmediatePropagation();

          const range = quill.getSelection(true);
          // Delete selected content first if there's a selection
          if (range.length > 0) {
            quill.deleteText(range.index, range.length, 'user');
          }
          quill.clipboard.dangerouslyPasteHTML(range.index, pastedText, 'user');
        }
      },
      { capture: true },
    );
  }
}
